import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TRFBirdMark, TRFWordmark } from '@/components/brand/TRFLogo';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { ThemeToggle } from '@/components/editorial/ThemeToggle';
// #2698: COARSE_HIT_44 is its own isolate context, so its hit area wins the
// hit test over the .topnav row (the -z-10 reach of COARSE_HIT_32/Y_36 sat
// under it and never took a tap).
import { COARSE_HIT_44 } from '@/lib/touch-target';
import { useAuth } from '@/contexts/AuthContext';
import { homeFor } from '@/lib/home-route';
import { ORG_NAME } from './org-name';

/**
 * `showApply`: the landing page passes false because its hero carries the
 * Apply to TRF button right under the bar (Victor 2026-10-20: the two were a
 * duplicate). `showSignIn`: the sign-in page passes false, with `showApply`,
 * because a Sign in button on the sign-in page goes nowhere and Apply is not
 * what a returning family came for (Victor 2026-10-21). `compactLanguage`:
 * the sign-in page shows the language icon alone; every other public page
 * keeps the named switcher ("English ▾") (Victor 2026-10-21).
 */
export function StevensTopbar({
  showApply = true,
  showSignIn = true,
  compactLanguage = false,
}: { showApply?: boolean; showSignIn?: boolean; compactLanguage?: boolean } = {}) {
  const { user, loading } = useAuth();
  // The public chrome's words (#2109). The Foundation's name is a proper noun:
  // it renders under translate="no" and reaches sentences as {{org}}.
  const { t } = useTranslation('shell');

  return (
    <div className="topbar">
      <div className="inner">
        {/* #881: the public brand chip is TRF identity, not the in-product
            companion persona (which keeps its name post-sign-in per #872). */}
        {/* The Foundation's official mark and wordmark (TRFLogo.tsx, from
            trf.org's logo); the link's aria-label carries the name. */}
        <Link to="/" className={`brand ${COARSE_HIT_44}`} aria-label={t('publicChrome.homeAria', { org: ORG_NAME })}>
          <TRFBirdMark height={30} />
          <TRFWordmark height={22} className="wordmark" />
        </Link>
        <nav className="topnav" aria-label={t('publicChrome.primaryNav')}>
          <ThemeToggle className={`border border-transparent hover:border-stevens-rule ${COARSE_HIT_44}`} />
          {/* B-163: the public chrome must agree with the page body about
              session state. Signed in → account link (homeFor = the same map
              the route guards use). Hydrating → an invisible same-class
              placeholder so the slot keeps its box (no "Sign in" flash, no
              Apply-button jump when auth resolves). Resolved signed-out →
              the original Sign in link. */}
          {!showSignIn ? null : user ? (
            <Link
              to={homeFor(user.role)}
              className={`signin whitespace-nowrap ${COARSE_HIT_44}`}
              title={t('publicChrome.signedInAs', { email: user.email })}
            >
              {t('publicChrome.myAccount')}
            </Link>
          ) : loading ? (
            <span aria-hidden="true" className="signin whitespace-nowrap invisible">
              {t('publicChrome.signIn')}
            </span>
          ) : (
            <Link to="/login" className={`signin whitespace-nowrap ${COARSE_HIT_44}`}>{t('publicChrome.signIn')}</Link>
          )}
          {/* #2109 R1 (L4): the anonymous language switcher lives in the bar
              on every public page (Victor 2026-10-20), beside Sign in. On the
              sign-in page it is the language icon alone (Victor 2026-10-21:
              "just the A + 文"); the open list still names each language. */}
          <LanguageSwitcher compact={compactLanguage} />
          {showApply && (
            <Link to="/apply" className={`btn ${COARSE_HIT_44}`}>{t('publicChrome.apply')}</Link>
          )}
        </nav>
      </div>
    </div>
  );
}
